/**
 * **One date format, in one place.**
 *
 * `MM/DD/YYYY` throughout, per **HR-2** in the house review checklist and
 * Nilay's instruction of 10 Sep. Before this module there were **six copies of a
 * near-identical formatter** — `ViewHeader`, `CoverageStrip`,
 * `ChannelDailyTables`, `ChannelOverview`, `Stories`, `Spotlights` — plus inline
 * calls in `Connections`, `Matches` and `Legacy`, rendering **three different
 * formats**: `Sep 9` on six surfaces, `Sep 9, 2026` on two, and
 * `September 9, 2026` in the Legacy header. Four of the six omitted the year
 * entirely, which is the defect Nilay reported: with multi-year data a bare
 * `Sep 9` says nothing about which year.
 *
 * ### UTC, always, and explicitly
 *
 * Snap's stat day is UTC. Rendering it in the viewer's zone would shift every
 * label by a day for anyone west of Greenwich — a channel's Monday becoming its
 * Sunday, silently, and only for some readers. Every function here passes
 * `timeZone: 'UTC'` and parses bare `YYYY-MM-DD` as UTC midnight rather than
 * letting the runtime guess.
 */

/** A bare `YYYY-MM-DD` is a *day*, not an instant — parse it as UTC midnight so
 *  it cannot drift into the previous day for a westward viewer. Anything
 *  carrying a time already has a zone. */
function asDate(iso: string): Date {
  return new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso)
}

/**
 * **The formatters are built once, not per call.**
 *
 * `toLocaleDateString(locale, options)` constructs an `Intl.DateTimeFormat`
 * every time, and these run once per table cell: a 115-row Channel Daily view
 * calls them a few hundred times per render. A CPU profile of one filter change
 * put `formatDate` at the top of the non-idle frames — small in absolute terms,
 * but it was the largest thing the app itself was doing, and caching an `Intl`
 * object is the standard remedy.
 */
const DAY = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'UTC',
})

const AXIS_DAY = new Intl.DateTimeFormat('en-US', {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'UTC',
})

/**
 * The app's date format: `08/19/2026`.
 *
 * Use this everywhere a date appears as text — table cells, status lines, KPI
 * captions, range read-outs, tooltips.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return DAY.format(asDate(iso))
}

/**
 * A date range: `08/11/2026 – 09/09/2026`.
 *
 * An en dash with hair spaces, not a hyphen — a hyphen inside two slashed dates
 * reads as part of a number.
 */
export function formatRange(from: string, to: string): string {
  return `${formatDate(from)} – ${formatDate(to)}`
}

/**
 * **A pipeline clock, in Pacific, with the UTC it is configured in — CN-7.**
 *
 * Abi, 15 Sep: *"when you hover over the next run time for any of the datasets,
 * can we have the time display in pacific standard time instead of UTC?"*
 *
 * This is the **one** exception to the UTC rule above, and it is deliberately
 * narrow: it applies to *instants the pipeline acted at or will act at*, never
 * to a date that names a Snap stat day. `/connections` is the only caller.
 * Rendering a stat date in Pacific would shift every label by a day for anyone
 * west of Greenwich, which is the defect this module exists to prevent.
 *
 * 🔴 **The zone name is emitted, never hardcoded.** Abi asked for "pacific
 * standard time" and for most of the year it is not PST — it is **PDT** until
 * 1 November. A literal `PST` would be an hour wrong for six weeks, on a screen
 * whose entire job is answering *"did last night's run happen?"*. `Intl` with
 * `timeZoneName: 'short'` gets this right on its own, every time.
 *
 * The UTC half stays because the schedule is *configured* in UTC — 02:00 snap,
 * 03:00 thumbnails, 04:00 clickup (`app/pipelines/schedule.py`). Dropping it
 * would leave the screen and the code with no shared number.
 */
const PIPELINE_MOMENT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'America/Los_Angeles',
  timeZoneName: 'short',
})

/**
 * `09/15/2026, 9:09 PM PDT` — **the visible form.**
 *
 * 🔴 **Split out because CN-7 shipped invisible.** The Pacific time existed but
 * only inside a `title` attribute, so `/connections` still read *"Data through
 * 09/14/2026 · ran 36h ago · next in 13h"* — a date and two relative spans, with
 * the clock behind a native tooltip that takes a second to appear and that
 * nobody hovers. Nilay, 16 Sep: *"we are not showing the time in pacific time —
 * we are just showing the date?"* Correct, and the fix is where it renders, not
 * what it renders.
 *
 * Full `MM/DD/YYYY` rather than a short date, per **HR-2**: at 36 hours stale
 * the day is the point, so dropping it would lose exactly the information the
 * reader came for.
 */
export function formatPipelineClock(iso: string | null | undefined): string {
  if (!iso) return '—'
  return PIPELINE_MOMENT.format(asDate(iso))
}

/** The same instant with the UTC the schedule is configured in — for the hover,
 *  where there is room to reconcile the screen against `schedule.py`. */
export function formatPipelineMoment(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = asDate(iso)
  // The UTC *time* only — repeating the date would be noise, since the two are
  // the same day or an hour apart from being so.
  const utc = UTC_TIME.format(date)
  return `${PIPELINE_MOMENT.format(date)} (${utc} UTC)`
}

const UTC_TIME = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
})

const READOUT_DAY = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
  timeZone: 'UTC',
})

/**
 * `Sep 09, 2026` — a chart's hover readout (CO-13, Nilay 28 Sep).
 *
 * The long form, per the Reporting Platform blueprint's `numbers-and-dates` rule:
 * *"`MM/DD/YYYY` or `Aug 16, 2026`, one form per surface. The long form is
 * preferred wherever there is room, because it cannot be misread as `DD/MM`."*
 * A recorded exception to HR-2 for this one surface. The two-digit day keeps
 * every date the same width, so nothing beside it shifts as the pointer moves.
 */
export function formatReadoutDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return READOUT_DAY.format(asDate(iso))
}

/**
 * `08/19/26` — **chart axis ticks and the coverage strip only.**
 *
 * The one documented departure from `MM/DD/YYYY`, and it is a legibility
 * constraint rather than a preference: a 30-day axis carries 30 ticks, and
 * `08/19/2026` at that density overlaps into an unreadable smear. The two-digit
 * year is what keeps it **unambiguous across years** — which is the whole reason
 * the year is being added, so dropping to a bare `08/19` would reintroduce the
 * defect at the one place a multi-year series is actually drawn.
 *
 * Never use this in a table cell or a sentence; `formatDate` is the default.
 */
export function formatAxisDate(iso: string | null | undefined): string {
  if (!iso) return ''
  return AXIS_DAY.format(asDate(iso))
}
